/**
 * Executive Producer Quips with Context Awareness
 *
 * Witty, sarcastic loading messages in the voice of a TV executive producer
 * reviewing a screenwriter's work. Positions users as "screenwriters" and their
 * messages as "scripts" to be evaluated.
 *
 * Voice: Sarcastic but not mean, industry-savvy, references TV/film production
 *
 * V8 Update (2025-01-13): Expanded to 533 quips (280 existing + 253 new conflict scenarios)
 * V9 Update (2025-12-12): Added pattern-matched quip selection for AI-reactive dialogue
 *
 * REFACTORED: 2026-01-07 - Split into 3 modules for better maintainability
 * - types.ts: QuipCategory, QuipEntry, PATTERN_TO_QUIP_CATEGORY, QUIP_CATEGORY_PREFIXES
 * - data.ts: PRODUCER_QUIPS constant (533 quip entries) - INTERNAL ONLY
 * - index.ts: Selection logic and public API
 */

import type { MessageContext } from '@dramadetector/shared';
import { PatternType } from '../dramaExpert/types';

// Import types and constants
import type {
  QuipCategory,
  QuipEntry,
} from './types';

import {
  PATTERN_TO_QUIP_CATEGORY,
  QUIP_CATEGORY_PREFIXES,
} from './types';

// Import data (INTERNAL - not re-exported)
import { PRODUCER_QUIPS } from './data';

/**
 * Calculate dynamic selection weight based on available context-specific quips
 *
 * PHASE 2: Dynamic Weighting with Pool Exhaustion Detection
 *
 * Adaptive algorithm that adjusts context-match probability based on pool availability:
 * - 10+ available: 70% context (strong personalization when pool is fresh)
 * - 6-9 available: 60% context (moderate personalization)
 * - 3-5 available: 50% context (balanced approach)
 * - 1-2 available: 30% context (graceful degradation to prevent fatigue)
 * - 0 available: 0% context (fallback to general quips)
 *
 * This prevents the jarring experience of 70% → 0% drop when pool exhausts,
 * providing smooth degradation as users see more repeated context-specific quips.
 *
 * @param availableContextCount - Number of context-specific quips available after cache filter
 * @returns Probability (0.0 to 1.0) of selecting context-matched quip
 */
function calculateContextWeight(availableContextCount: number): number {
  if (availableContextCount >= 10) return 0.7; // 70% - Fresh pool, strong personalization
  if (availableContextCount >= 6) return 0.6; // 60% - Moderate pool
  if (availableContextCount >= 3) return 0.5; // 50% - Balanced (Phase 1 baseline)
  if (availableContextCount >= 1) return 0.3; // 30% - Depleted pool, graceful degradation
  return 0.0; // 0% - Exhausted pool, use general quips only
}

/**
 * Get a random quip from the pool, with optional context-aware filtering
 *
 * V8 IMPLEMENTATION (Dynamic Weighting + Conflict Scenarios):
 * - 533 total quips: 80 general + 200 context-aware (50 per context) + 253 conflict scenarios
 * - Dynamic weighting based on pool availability (70% → 60% → 50% → 30% → 0%)
 * - Graceful degradation prevents fatigue for power users
 * - Session cache (5 quips) excludes recent repeats
 *
 * Expected Performance (with 50-quip context pools + 333 general pool):
 * - Analyses #1-45: 70% context-matched (10-50 available after cache)
 * - Analyses #46-50: 60% context-matched (6-9 available)
 * - Analyses #51-55: 50% context-matched (3-5 available)
 * - Analyses #56-60: 30% context-matched (1-2 available)
 * - Analyses #61+: 0% context-matched (fallback to 333 general quips: 80 original + 253 conflict scenarios)
 *
 * Fatigue Timeline:
 * - Phase 1 (4-5 quips): Repeat at analysis #6-7
 * - Phase 2 (50 quips per context): Repeat at analysis #70-75
 * - V8 (533 total quips): Repeat at analysis #500+ for general pool (4x improvement)
 * - Improvement: 10-12x longer before perceived repetition in context-aware mode
 *
 * @param excludeRecent - Array of quip keys to exclude (recently shown)
 * @param context - Optional message context to prefer matching quips
 * @returns Random quip translation key
 */
export function getRandomQuip(
  excludeRecent: string[] = [],
  context?: MessageContext
): string {
  // Filter out recently shown quips
  const availableQuips = PRODUCER_QUIPS.filter(
    (quip) => !excludeRecent.includes(quip.key)
  );

  // If no available quips (all recently shown), reset to full pool
  const quipsToUse = availableQuips.length > 0 ? availableQuips : PRODUCER_QUIPS;

  // If no context provided, return random quip from available pool
  if (!context) {
    const randomIndex = Math.floor(Math.random() * quipsToUse.length);
    return quipsToUse[randomIndex].key;
  }

  // Split into context-matched dogfooding quips and general quips
  const contextMatched = quipsToUse.filter(
    (quip) => quip.contexts && quip.contexts.includes(context)
  );
  const general = quipsToUse.filter((quip) => !quip.contexts);

  // If no context-matched quips available, use general quips
  if (contextMatched.length === 0) {
    const randomIndex = Math.floor(Math.random() * general.length);
    return general[randomIndex].key;
  }

  // PHASE 2: Dynamic weighting based on pool availability
  // Calculates adaptive probability to prevent fatigue while maintaining personalization
  const contextWeight = calculateContextWeight(contextMatched.length);
  const useContextMatch = Math.random() < contextWeight;

  const selectedPool =
    useContextMatch && contextMatched.length > 0 ? contextMatched : general;

  // Fallback to other pool if selected pool is empty
  const finalPool =
    selectedPool.length > 0
      ? selectedPool
      : contextMatched.length > 0
      ? contextMatched
      : general;

  const randomIndex = Math.floor(Math.random() * finalPool.length);
  return finalPool[randomIndex].key;
}

/**
 * Get the total number of available quips
 */
export function getQuipCount(): number {
  return PRODUCER_QUIPS.length;
}

/**
 * Get count of context-aware dogfooding quips
 */
export function getDogfoodingQuipCount(): number {
  return PRODUCER_QUIPS.filter((quip) => quip.isDogfooding).length;
}

/**
 * Get count of context-specific quips for a given context
 * Useful for analytics and debugging
 */
export function getContextQuipCount(context: MessageContext): number {
  return PRODUCER_QUIPS.filter(
    (quip) => quip.contexts && quip.contexts.includes(context)
  ).length;
}

/**
 * Validate that all quip keys follow the expected format
 * Used for testing/debugging
 */
export function validateQuipKeys(): boolean {
  return PRODUCER_QUIPS.every(
    (quip) =>
      quip.key.startsWith('producer_quips.') &&
      quip.key.length > 'producer_quips.'.length
  );
}

/**
 * Simple hash function to create deterministic selection from a string
 * Used to ensure same message always gets same quip (prevents repetition feeling random)
 *
 * @param str - String to hash (e.g., message text or analysis ID)
 * @returns Positive integer hash value
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a quip from a specific conflict scenario category
 *
 * @param category - The quip category to select from
 * @param seed - Optional seed for deterministic selection (e.g., message hash)
 * @returns Quip key from the specified category, or undefined if category is empty
 */
function getQuipByCategory(
  category: QuipCategory,
  seed?: string
): string | undefined {
  const prefixes = QUIP_CATEGORY_PREFIXES[category];

  // Filter quips that match any prefix for this category
  const categoryQuips = PRODUCER_QUIPS.filter((quip) =>
    prefixes.some((prefix) => quip.key.startsWith(prefix))
  );

  if (categoryQuips.length === 0) {
    return undefined;
  }

  // Use deterministic selection if seed provided, otherwise random
  const index = seed
    ? simpleHash(seed) % categoryQuips.length
    : Math.floor(Math.random() * categoryQuips.length);

  return categoryQuips[index].key;
}

/**
 * Get count of quips in a specific conflict scenario category
 * Useful for analytics and debugging
 *
 * @param category - The quip category to count
 * @returns Number of quips in the category
 */
export function getCategoryQuipCount(category: QuipCategory): number {
  const prefixes = QUIP_CATEGORY_PREFIXES[category];
  return PRODUCER_QUIPS.filter((quip) =>
    prefixes.some((prefix) => quip.key.startsWith(prefix))
  ).length;
}

/**
 * Get a pattern-matched quip based on detected drama patterns
 *
 * V9 IMPLEMENTATION (AI-Reactive Dialogue):
 * - Maps PatternType enum values to conflict scenario quip categories
 * - Uses first matching pattern (priority order from analysis)
 * - Deterministic selection using seed (message hash) for consistency
 * - Fallback to context-aware or general quips if no pattern match
 *
 * Selection Priority:
 * 1. Pattern-matched quip (if detected patterns map to a category)
 * 2. Context-aware quip (if context provided)
 * 3. General quip pool (fallback)
 *
 * @param patterns - Array of detected PatternType values from analysis
 * @param seed - Optional seed for deterministic selection (e.g., analysisId, message hash)
 * @param context - Optional message context for fallback
 * @param excludeRecent - Array of recently shown quip keys to avoid
 * @returns Quip translation key
 *
 * @example
 * ```typescript
 * // In letterZoom Convergence phase:
 * const quip = getPatternQuip(
 *   analysisResult.patterns,      // ['gaslighting_potential', 'defensiveness']
 *   analysisResult.id,            // Deterministic seed
 *   messageContext,               // 'dating' | 'family' | etc.
 *   recentQuips                   // Last 5 shown quips
 * );
 * // Returns: 'producer_quips.gaslighting_overnight_rewrite_01'
 * // (gaslighting quip because it's first matching pattern)
 * ```
 */
export function getPatternQuip(
  patterns: PatternType[] = [],
  seed?: string,
  context?: MessageContext,
  excludeRecent: string[] = []
): string {
  // Try to find a matching category from detected patterns
  for (const pattern of patterns) {
    const category = PATTERN_TO_QUIP_CATEGORY[pattern];
    if (category) {
      const quip = getQuipByCategory(category, seed);
      if (quip && !excludeRecent.includes(quip)) {
        return quip;
      }
    }
  }

  // Fallback: Use context-aware or general quip
  return getRandomQuip(excludeRecent, context);
}

/**
 * Check if any of the provided patterns have a matching quip category
 * Useful to determine if pattern-matched quip is available before Convergence phase
 *
 * @param patterns - Array of detected PatternType values
 * @returns true if at least one pattern maps to a quip category
 */
export function hasPatternQuip(patterns: PatternType[]): boolean {
  return patterns.some((pattern) => PATTERN_TO_QUIP_CATEGORY[pattern] !== undefined);
}

// ============================================================
// PUBLIC API EXPORTS
// ============================================================

// Re-export types
export type { QuipCategory, QuipEntry } from './types';

// Re-export constants
export { PATTERN_TO_QUIP_CATEGORY, QUIP_CATEGORY_PREFIXES } from './types';

// NOTE: PRODUCER_QUIPS is INTERNAL ONLY - not re-exported
// This prevents bundle bloat when consumers only need types/functions
